import "./Estimate.css";
import { useEffect, useState } from "react";
import { Content, Header, Main } from "../../commons/Template/Template";
import api from "../../../api/resources";
import Button from "../../commons/Button/Button";
import { Cancel, Check, Edit, Info, Send } from "@mui/icons-material";
import { formatAmount, formatDate } from "../../../format";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import routes from "../../../routes/routes";
import { useModalDispatch } from "../../commons/Modal/ModalContext";
import sub from "../../../assets/subscription/sub.png";
import ImageContainer from "../../commons/ImageContainer/ImageContainer";
import {
  Table,
  TableBody,
  TableRow,
  TableRowHeader,
  TableRowItem,
  TableWrapper,
} from "../../commons/Table/Table";
import usePartners from "../usePartners";
import useInvoiceCheck from "../useInvoiceCheck";
import Tabs from "../../commons/Tabs/Tabs";
import Typo from "../../commons/Typo/Typo";

export default function Estimate({ loanRequestID, onEdit }) {
  const navigate = useNavigate();

  const { t, i18n } = useTranslation(["common", "financing"]);
  document.title = t("financing:estimate.title");
  const dispatchModal = useModalDispatch();

  const [loading, setLoading] = useState(true);
  const [tabs, setTabs] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState("");
  const [proposals, setProposals] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [readyForRequest, setReadyForRequest] = useState(false);
  const [requesting, setRequesting] = useState(false);

  const getCommonT = (word) => t(`common:${word}`);

  const handleLoanRequestChecks = async (loanRequest) => {
    let allInvoicesComplete = true;
    const invoiceBasedUCs = ["short-term_invoice", "multi_invoice"];
    if (invoiceBasedUCs.includes(loanRequest.use_case)) {
      if (loanRequest.invoices.length === 0) {
        allInvoicesComplete = false;
      } else if (loanRequest.invoices.length > 0) {
        let invoices = [];
        for (const invoiceID of loanRequest.invoices) {
          const invoiceResponse = await api.getInvoice(invoiceID);
          if (invoiceResponse.ok) {
            const jsonInvoice = await invoiceResponse.json();
            invoices.push(jsonInvoice);
          }
        }
        allInvoicesComplete = invoices.reduce(
          (prev, current) => prev && useInvoiceCheck(current),
          true,
        );
      }
    }

    let ready = allInvoicesComplete && (loanRequest.amount ? true : false);
    setReadyForRequest(ready);
    return ready;
  };

  const partners = usePartners();

  const init = async () => {
    setLoading(true);

    let ready = true;
    const loanRequestResponse = await api.getLoanRequest(loanRequestID);
    if (loanRequestResponse.ok) {
      const jsonLoanRequest = await loanRequestResponse.json();
      ready = await handleLoanRequestChecks(jsonLoanRequest);
    }

    if (!ready) {
      onEdit();
    } else {
      const response = await api.estimateLoanRequest(loanRequestID);
      if (response.ok) {
        const jsonEstimates = await response.json();

        // Init. tabs
        setTabs(
          jsonEstimates.map((estimate) => ({
            id: estimate.provider,
            title: partners.find((partner) => partner.id === estimate.provider)
              .name,
          })),
        );
        setSelectedProvider(jsonEstimates[0].provider);

        const lang = i18n.resolvedLanguage;

        const formatAmountFn = (amount, currency) =>
          amount ? formatAmount(amount, lang, currency) : "-";

        // Init. proposals
        setProposals(
          jsonEstimates.map((estimate) => {
            const currency = estimate.currency;
            return {
              id: estimate.provider,
              partnerRequestID: estimate.id,
              response: estimate.response,
              responseDetails: estimate.details,
              capped:
                estimate.amount.is_capped ||
                (estimate.months && estimate.months.is_capped) ||
                (estimate.days && estimate.days.is_capped),
              items: {
                inputs: [
                  {
                    label: getCommonT("amount"),
                    value: formatAmountFn(estimate.amount.value, currency),
                    capped: estimate.amount.is_capped,
                  },
                  {
                    label: getCommonT("creditDate"),
                    value: formatDate(estimate.starting_date, lang),
                  },
                  estimate.months
                    ? {
                        label: getCommonT("duration"),
                        value: `${estimate.months.value} ${
                          estimate.months.value > 1
                            ? getCommonT("months")
                            : getCommonT("month")
                        }`,
                        capped: estimate.months.is_capped,
                      }
                    : {
                        label: getCommonT("duration"),
                        value: `${estimate.days.value} ${
                          estimate.days.value > 1
                            ? getCommonT("days")
                            : getCommonT("day")
                        }`,
                        capped: estimate.days.is_capped,
                      },
                ],
                outputs: [
                  {
                    label: getCommonT("totalCost"),
                    value: formatAmountFn(estimate.cost, currency),
                  },
                  {
                    label: getCommonT("upfrontFee"),
                    value: formatAmountFn(estimate.upfront_fee, currency),
                  },
                  {
                    label: getCommonT("interest"),
                    value: formatAmountFn(estimate.interest, currency),
                  },
                  {
                    label: getCommonT("fees"),
                    value: formatAmountFn(estimate.fee, currency),
                  },
                ],
                received: [
                  {
                    label: t(
                      "financing:estimate.proposal.items.received_amount",
                    ),
                    value: formatAmountFn(estimate.received_amount, currency),
                  },
                ],
              },
            };
          }),
        );

        // Init. schedules
        setSchedules(
          jsonEstimates.map((estimate) => {
            const currency = estimate.currency;

            return {
              id: estimate.provider,
              rows: estimate.schedule.map((term) => ({
                items: [
                  {
                    label: getCommonT("Date"),
                    value: formatDate(term.date, lang),
                  },
                  {
                    label: getCommonT("amount"),
                    value: formatAmountFn(term.amount, currency),
                    className: "styleTdComponent",
                  },
                  {
                    label: getCommonT("capital"),
                    value: formatAmountFn(term.capital, currency),
                  },
                  {
                    label: getCommonT("interest"),
                    value: formatAmountFn(term.interest, currency),
                  },
                  {
                    label: getCommonT("remainingAmountDue"),
                    value: formatAmountFn(term.remaining, currency),
                  },
                ],
              })),
            };
          }),
        );
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    init();
  }, []);

  const updateProposals = (
    partnerRequestID,
    newResponse,
    newResponseDetails = [],
  ) => {
    setProposals((prevProposals) => {
      return [
        ...prevProposals.filter(
          (proposal) => proposal.partnerRequestID !== partnerRequestID,
        ),
        {
          ...prevProposals.find(
            (proposal) => proposal.partnerRequestID === partnerRequestID,
          ),
          response: newResponse,
          responseDetails: newResponseDetails,
        },
      ];
    });
  };

  const handleNoSubscription = () => {
    const modal = {
      component: <SubscribeModal />,
      size: "medium",
      onSuccess: () => navigate(routes.Settings.subscription),
    };
    dispatchModal({ type: "add", ...modal });
  };

  const handleRequestLoan = async (partnerRequestID) => {
    setRequesting(true);
    const response = await api.requestLoan(partnerRequestID);
    let JSONResponse;
    switch (response.status) {
      case 200:
        updateProposals(partnerRequestID, "pending");
        break;
      case 202:
        JSONResponse = await response.json();
        updateProposals(
          partnerRequestID,
          JSONResponse.accepted ? "accepted" : "pending",
        );
        break;
      case 400:
        JSONResponse = await response.json();
        updateProposals(partnerRequestID, "errors", JSONResponse.errors);
        break;
      case 402:
        handleNoSubscription();
        break;
      case 422:
        JSONResponse = await response.json();
        updateProposals(partnerRequestID, "refused", JSONResponse.reason);
        break;
      case 500:
        updateProposals(partnerRequestID, "errors", { errors: ["Errore"] }); // ??
        break;
      default:
        console.log(response);
        break;
    }
    setRequesting(false);
  };

  const filterFn = (object) => object.id === selectedProvider;

  if (loading) return <></>;

  return (
    <Main themeSwitch className="Estimate">
      <Header>
        <Typo variant="h1">{t("financing:estimate.title")}</Typo>
      </Header>
      <Tabs
        tabs={tabs}
        selectedID={selectedProvider}
        onSelect={setSelectedProvider}
        smallTabs
      />
      <Content
        style={{
          display: "flex",
          gap: "2.5rem",
          flexWrap: "wrap",
        }}
      >
        <div style={{ flexBasis: "22rem", flexGrow: "0.1" }}>
          {proposals.length > 0 && (
            <Proposal
              readyForRequest={readyForRequest}
              {...proposals.find(filterFn)}
              onRequest={() =>
                handleRequestLoan(proposals.find(filterFn).partnerRequestID)
              }
              requesting={requesting}
              editable={proposals.every(
                (proposal) =>
                  proposal.response === "refused" ||
                  proposal.response === "errors" ||
                  !proposal.response,
              )}
              onEdit={onEdit}
            />
          )}
        </div>
        <div
          style={{
            flexBasis: "25rem",
            flexGrow: "150",
            overflow: "auto",
          }}
        >
          {schedules.length > 0 && (
            <Schedule>
              <ScheduleCaption
                captionTitle={t("common:schedule")}
                captionSubtitle={t("financing:estimate.schedule.subtitle")}
              />
              <TableWrapper>
                <Table>
                  <TableBody>
                    {schedules.find(filterFn).rows.map((row, index) => (
                      <TableRow key={index}>
                        <TableRowHeader
                          thLabel={t("common:term")}
                          thValue={index + 1}
                        />
                        {row.items.map((item, itemIndex) => (
                          <TableRowItem key={itemIndex} {...item} />
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableWrapper>
            </Schedule>
          )}
        </div>
      </Content>
    </Main>
  );
}

function Proposal({
  items = null,
  onRequest = null,
  requesting = false,
  response = "",
  responseDetails = [],
  editable = true,
  onEdit = null,
  readyForRequest,
  capped = false,
}) {
  return (
    <div className="Proposal">
      <ProposalCard>
        {capped && <ProposalCardNote />}
        <ProposalCardItemsContainer>
          <ProposalCardItems items={items.inputs} />
          <ProposalCardItems items={items.outputs} />
          <ProposalCardItems items={items.received} />
        </ProposalCardItemsContainer>
      </ProposalCard>
      <ProposalResponse
        readyForRequest={readyForRequest}
        onRequest={onRequest}
        requesting={requesting}
        response={response}
        responseDetails={responseDetails}
        editable={editable}
        onEdit={onEdit}
      />
    </div>
  );
}

function ProposalCard({ children }) {
  const { t } = useTranslation(["common", "financing"]);

  return (
    <div className="Proposal-card">
      <div className="Proposal-card-header">
        <div className="Proposal-card-header-heading">
          {t("financing:estimate.proposal.heading")}
        </div>
        <div className="Proposal-card-header-subheading">
          {t("financing:estimate.proposal.subheading")}
        </div>
      </div>
      {children}
    </div>
  );
}

function ProposalCardNote() {
  const { t } = useTranslation(["common", "financing"]);
  return (
    <div className="Proposal-card-note">
      <Typo className="Proposal-card-note-title">
        <Info fontSize="x-small" />{" "}
        {t("financing:estimate.proposal.note.title")}
      </Typo>
      <Typo className="Proposal-card-note-content">
        {t("financing:estimate.proposal.note.content")}
      </Typo>
    </div>
  );
}

function ProposalCardItemsContainer({ children }) {
  return <div className="Proposal-card-items-container">{children}</div>;
}

function ProposalCardItems({ items }) {
  return (
    <div className="Proposal-card-items">
      {items.map((item, index) => (
        <ProposalCardItem key={index} {...item} />
      ))}
    </div>
  );
}

function ProposalCardItem({ label, value, capped }) {
  return (
    <div
      key={label}
      className={["Proposal-card-items-item", capped && "capped"]
        .filter(Boolean)
        .join(" ")}
    >
      <span className="Proposal-card-items-item-label">
        {label} {capped && <Info fontSize="xx-small" />}
      </span>
      <span className="Proposal-card-items-item-value">{value}</span>
    </div>
  );
}

function ProposalResponse({
  readyForRequest = true,
  onRequest = null,
  requesting = false,
  response = "",
  responseDetails = [],
  editable = true,
  onEdit = null,
}) {
  const { t } = useTranslation(["common", "financing"]);

  const NOK = ["errors", "refused"].some((res) => res === response);
  const OK = ["pending", "accepted"].some((res) => res === response);

  return (
    <div>
      <fieldset disabled={requesting}>
        <form
          className="Proposal-response"
          onSubmit={(e) => e.preventDefault()}
        >
          {!readyForRequest ? null : !response ? (
            <Button
              className="Proposal-response-buttons-primary"
              variant="primary_a"
              text={
                !requesting
                  ? t("financing:estimate.proposal.sendButton")
                  : t("common:loading")
              }
              iconLeft
              icon={<Send />}
              onClick={onRequest}
              disabled={requesting}
            />
          ) : NOK ? (
            <Button
              className="Proposal-response-buttons-primary"
              variant="error"
              text={t(
                `financing:estimate.proposal.responses.${response}.button`,
              )}
              iconLeft
              icon={<Cancel />}
              disabled
            />
          ) : OK ? (
            <Button
              className="Proposal-response-buttons-primary"
              variant="primary_a"
              text={t(
                `financing:estimate.proposal.responses.${response}.button`,
              )}
              iconLeft
              icon={<Check />}
              disabled
              style={{
                backgroundColor: "var(--color-success-1)",
                borderColor: "transparent",
              }}
            />
          ) : null}
          {response && (
            <div
              className="Proposal-response-details"
              style={{
                borderColor: OK
                  ? "var(--color-success-1)"
                  : NOK
                    ? "var(--color-error-1)"
                    : "",
              }}
            >
              <div className="Proposal-response-details-heading">
                {t(
                  `financing:estimate.proposal.responses.${response}.details.heading`,
                )}
              </div>
              <div className="Proposal-response-details-subheading">
                {response === "pending" ? (
                  <Trans
                    t={t}
                    i18nKey={`financing:estimate.proposal.responses.${response}.details.subheading`}
                    components={{
                      anchor: <Link to={routes.Financing.loans} />,
                    }}
                  />
                ) : response === "accepted" ? (
                  <Trans
                    t={t}
                    i18nKey={`financing:estimate.proposal.responses.${response}.details.subheading`}
                  />
                ) : response === "errors" ? (
                  <ul>
                    {responseDetails.map((detail, index) => (
                      <li key={index}>
                        {t(
                          `financing:estimate.proposal.responses.${response}.details.keys.${detail}`,
                        )}
                      </li>
                    ))}
                  </ul>
                ) : response === "refused" ? (
                  t(
                    `financing:estimate.proposal.responses.${response}.details.keys.${responseDetails}`,
                  )
                ) : null}
              </div>
            </div>
          )}
          {editable && (
            <Button
              variant="secondary_b"
              text={t("financing:estimate.proposal.modifyButton")}
              iconLeft
              icon={<Edit />}
              onClick={onEdit}
              disabled={requesting}
            />
          )}
        </form>
      </fieldset>
    </div>
  );
}

function SubscribeModal() {
  const { t } = useTranslation(["common", "financing"]);
  const dispatchModal = useModalDispatch();

  return (
    <div className="SubscribeModal">
      <ImageContainer
        src={sub}
        alt="subscription-img"
        className="SubscribeModal-ImageContainer"
      />
      <div className="SubscribeModal-text">
        <div className="SubscribeModal-text-heading">
          {t("financing:estimate.proposal.modals.subscribe.heading")}
        </div>
        <div className="SubscribeModal-text-subheading">
          {t("financing:estimate.proposal.modals.subscribe.subheading")}
        </div>
      </div>
      <Button
        className="SubscribeModal-button"
        variant="primary_a"
        text={t("financing:estimate.proposal.modals.subscribe.button")}
        onClick={() => dispatchModal({ type: "success" })}
      />
    </div>
  );
}

export function Schedule({ children }) {
  return <div className="Schedule">{children}</div>;
}

export function ScheduleCaption({ captionTitle, captionSubtitle }) {
  return (
    <div className="Schedule-caption">
      {captionTitle && (
        <div className="Schedule-caption-title">{captionTitle}</div>
      )}
      {captionSubtitle && (
        <div className="Schedule-caption-subtitle">{captionSubtitle}</div>
      )}
    </div>
  );
}
